import type { Json } from "@/lib/supabase/types";
import { supabase } from "@/lib/supabase/client";

export type CodeAuditAction =
  | "code_viewed"
  | "code_copied"
  | "code_created"
  | "code_updated"
  | "code_deleted"
  | "member_created"
  | "member_updated"
  | "member_deleted";
export type CodeAuditType = "totp" | "email" | "member";

export interface CodeAuditPayload {
  userId: string;
  userEmail: string;
  action: CodeAuditAction;
  codeType: CodeAuditType;
  referenceId?: string | null;
  codeLabel?: string | null;
  /** Alterações (ex.: { name: [antigo, novo] }) */
  details?: Json | null;
}

/**
 * Registra ação sensível (visualização ou cópia de código) para auditoria.
 * Timestamp com segundos é gravado no servidor (created_at).
 * Nunca exibir toast de "código copiado" sem ter chamado code_copied antes.
 */
export async function logCodeAction(payload: CodeAuditPayload): Promise<void> {
  const { userId, userEmail, action, codeType, referenceId, codeLabel, details } = payload;

  try {
    const { data: userTeams } = await supabase
      .from("user_teams")
      .select("teams(name)")
      .eq("user_id", userId);

    const teamNames = (userTeams ?? [])
      .map((ut: { teams?: { name: string } | { name: string }[] }) => {
        const t = Array.isArray(ut.teams) ? ut.teams[0] : ut.teams;
        return t?.name ?? "";
      })
      .filter(Boolean)
      .join(", ");

    await supabase.from("code_audit_log").insert({
      user_id: userId,
      user_email: userEmail,
      team_names: teamNames || "",
      action,
      code_type: codeType,
      reference_id: referenceId ?? null,
      code_label: codeLabel ?? null,
      details: details ?? null,
    });
  } catch (err) {
    console.error("[codeAuditLog] Failed to log action:", err);
    // Não falhar a UI; log pode ser inspecionado depois
  }
}

/** Formato de data/hora com segundos para exibição (ex: 2026-02-25 16:02:03) */
export function formatAuditTimestamp(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const h = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  const s = String(date.getSeconds()).padStart(2, "0");
  return `${y}-${m}-${d} ${h}:${min}:${s}`;
}
